/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import user from 'js/lib/user';

import EoiWidgetContainer from 'bundles/expression-of-interest/components/xddp/EoiWidgetContainer';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonType } from 'bundles/expression-of-interest/types';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import EnrollmentChecklist from 'bundles/premium-certificate/components/EnrollmentChecklist';
import withEnrollmentChecklistData from 'bundles/premium-certificate/components/EnrollmentChecklistDataHOC';
import { checklistStatusEnum } from 'bundles/premium-certificate/constants/constants';
import type {
  EnrollmentChecklistDataProps,
  EnrollmentChecklistItem,
} from 'bundles/premium-certificate/constants/types';
import type { EnrollmentFlowProps } from 'bundles/premium-certificate/types';
import { trackApplyClick, trackEmailMeInfoClick } from 'bundles/premium-certificate/utils';
import { getRegHelpText } from 'bundles/premium-certificate/utils/checklistUtils';

import _t from 'i18n!nls/premium-certificate';

type Props = {
  enrollmentChecklistItems?: Array<EnrollmentChecklistItem>;
  enrollmentChecklistHeading?: string | null;
} & EnrollmentFlowProps &
  EnrollmentChecklistDataProps;

const handleEnrollButtonClick = async (
  slug: $TSFixMe,
  onEnrollButtonClick: $TSFixMe,
  enrollmentChecklistStatus: $TSFixMe,
  mastertrackId: string | undefined,
  certificateName: string | undefined,
  certificateType: Maybe<string> | undefined
) => {
  trackApplyClick(slug, mastertrackId, certificateName, certificateType);

  const hasChecklistNotStarted =
    !!onEnrollButtonClick && enrollmentChecklistStatus === checklistStatusEnum.checklistNotStartedYet;

  if (hasChecklistNotStarted) {
    await onEnrollButtonClick();
  }
};

const isChecklistInProgress = (
  enrollmentChecklistHeading: $TSFixMe,
  enrollmentChecklistItems: $TSFixMe,
  enrollmentChecklistStatus: $TSFixMe
) => {
  if (!enrollmentChecklistStatus) {
    return false;
  }

  const areChecklistItemsDefined = !!enrollmentChecklistHeading && !!enrollmentChecklistItems;
  const hasChecklistStarted =
    enrollmentChecklistStatus === checklistStatusEnum.checklistStarted ||
    enrollmentChecklistStatus === checklistStatusEnum.checklistCompleted;

  return areChecklistItemsDefined && hasChecklistStarted;
};

export const ChecklistFlow = ({
  actionBarData,
  slug,
  expressionOfInterestContainer,
  setEmbeddedRegFormDisplayed,
  enrollmentChecklistHeading,
  enrollmentChecklistItems,
  isLoadingChecklistData,
  enrollmentChecklistStatus,
  enrollmentChecklistItemStatuses,
  mastertrackId,
  certificateName,
  certificateType,
  onEnrollButtonClick,
}: Props) => {
  if (isLoadingChecklistData) {
    return null;
  }

  const isInProgress = isChecklistInProgress(
    enrollmentChecklistHeading,
    enrollmentChecklistItems,
    enrollmentChecklistStatus
  );

  if (isInProgress) {
    const emailAddress = user.get().email_address;
    const helpText = getRegHelpText(emailAddress);
    setEmbeddedRegFormDisplayed(true);

    return (
      <div
        data-test="ChecklistFlow"
        className="section-embedded"
        css={css`
          margin-bottom: 32px;
          padding: 24px;
        `}
      >
        <EnrollmentChecklist
          className="text-primary"
          helpText={helpText}
          enrollmentChecklistItemStatuses={enrollmentChecklistItemStatuses}
          enrollmentChecklistItems={enrollmentChecklistItems}
          enrollmentChecklistHeading={enrollmentChecklistHeading}
          enrollmentChecklistStatus={enrollmentChecklistStatus}
        />
      </div>
    );
  }

  const enrollButtonText = expressionOfInterestContainer?.applyButtonText;
  const enrollButton: EoiButtonType = {
    actionType: ACTION_TYPES.apply,
    doAction: () =>
      handleEnrollButtonClick(
        slug,
        onEnrollButtonClick,
        enrollmentChecklistStatus,
        mastertrackId,
        certificateName,
        certificateType
      ),
    isInChecklistMode: true,
    buttonOptions: {
      label: enrollButtonText || _t('Enroll'),
      trackingName: 'enroll',
      trackingData: { section: 'headerBanner' },
    },
  };

  const emailMeInfoButton: EoiButtonType = {
    actionType: ACTION_TYPES.emailMeInfo,
    doAction: () => trackEmailMeInfoClick(slug, mastertrackId, certificateName, certificateType),
  };

  return (
    <div data-test="ChecklistFlow">
      <EoiWidgetContainer
        slug={slug}
        degreeId={mastertrackId || ''}
        actionBarData={actionBarData}
        applicationUrl={expressionOfInterestContainer?.applyButtonUrl || ''}
        programInterestText={expressionOfInterestContainer?.programInterestText || ''}
        enrollButtonData={enrollButton}
        emailMeInfoButtonData={emailMeInfoButton}
      />
    </div>
  );
};

export default withEnrollmentChecklistData(ChecklistFlow);
